import { Settings2, RotateCcw } from "lucide-react";
import { useImageModelStore } from "@/store/imageModelStore";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const InlineImageModelParams = () => {
  const {
    selectedModel,
    getSelectedModel,
    getCurrentModelParameters,
    updateModelParameter,
    resetCurrentModelParameters,
  } = useImageModelStore();

  const model = getSelectedModel();
  const parameters = getCurrentModelParameters();

  if (!model || !model.parameters) return null;

  const paramEntries = Object.entries(model.parameters);
  if (paramEntries.length === 0) return null;

  const renderParameter = (paramName, paramConfig) => {
    // Use stored value or fall back to config default
    const value = parameters[paramName] ?? paramConfig.default;

    switch (paramConfig.type) {
      case "select":
        return (
          <div key={paramName} className="space-y-1.5">
            <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">
              {paramConfig.label}
            </Label>
            <Select
              value={String(value ?? "")}
              onValueChange={(newValue) =>
                updateModelParameter(selectedModel, paramName, newValue)
              }
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-[100001]">
                {paramConfig.options.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={String(option.value)}
                    className="text-xs"
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case "number": {
        const defaultNum = paramConfig.default ?? paramConfig.min ?? 1;
        const numberValue = Number(value) || defaultNum;
        return (
          <div key={paramName} className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                {paramConfig.label}
              </Label>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {numberValue}
              </span>
            </div>
            <Slider
              value={[numberValue]}
              onValueChange={([newValue]) =>
                updateModelParameter(selectedModel, paramName, newValue)
              }
              min={paramConfig.min || 0}
              max={paramConfig.max || 100}
              step={paramConfig.step || 1}
              className="w-full"
            />
          </div>
        );
      }

      case "text": {
        return (
          <div key={paramName} className="space-y-1.5">
            <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">
              {paramConfig.label}
            </Label>
            <Textarea
              value={value || ""}
              onChange={(e) =>
                updateModelParameter(selectedModel, paramName, e.target.value)
              }
              placeholder={paramConfig.description || "Optional"}
              className="min-h-[60px] resize-none text-xs"
            />
          </div>
        );
      }

      case "boolean": {
        return (
          <div
            key={paramName}
            className="flex items-center justify-between py-1"
          >
            <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">
              {paramConfig.label}
            </Label>
            <Switch
              checked={Boolean(value)}
              onCheckedChange={(checked) =>
                updateModelParameter(selectedModel, paramName, checked)
              }
              className="scale-90"
            />
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="h-[26px] px-2 flex items-center gap-1 rounded-md border border-[#2563eb]/30 dark:border-[#99CEFF]/20 bg-[#2563eb]/10 dark:bg-[#99CEFF]/10 hover:bg-[#2563eb]/20 dark:hover:bg-[#99CEFF]/20 text-[#2563eb] dark:text-[#99CEFF] transition-colors duration-150"
          title="Image parameters"
        >
          <Settings2 className="h-3.5 w-3.5" />
          <span className="hidden sm:inline text-xs font-medium">Options</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-72 p-3 z-[100000]"
        align="start"
        sideOffset={8}
      >
        <div className="space-y-3">
          <div className="flex items-center justify-between pb-2 border-b border-gray-200 dark:border-gray-700">
            <h4 className="text-xs font-semibold text-gray-900 dark:text-gray-100">
              {model.name} Options
            </h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={resetCurrentModelParameters}
              className="h-6 px-1.5 text-xs"
            >
              <RotateCcw className="h-3 w-3 mr-1" />
              Reset
            </Button>
          </div>

          <div className="space-y-3">
            {paramEntries.map(([paramName, paramConfig]) =>
              renderParameter(paramName, paramConfig)
            )}
          </div>

          {/* Model limits info */}
          {model.limits && (
            <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
              <div className="text-[10px] text-gray-500 dark:text-gray-400 flex gap-3">
                {model.limits.maxImages && (
                  <span>Max: {model.limits.maxImages} images</span>
                )}
                {model.limits.maxPromptLength && (
                  <span>Prompt: {model.limits.maxPromptLength} chars</span>
                )}
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
